import { useMutation } from "@tanstack/react-query";
import { AlertTriangle } from "lucide-react";
import React from "react";
import { toast } from "sonner";

import * as fbs from "@/api/fbs";
import { Button } from "@/components/ui/button";
import { sgtinShowSupply, sgtinStickerLabel, sgtinSupplyLabel } from "@/lib/fbs";
import { apiErrorText } from "@/lib/scan-ui/screen";
import { RemoveSgtinButton } from "@/pages/fbs/RemoveSgtinButton";

/** Постоянное красное сообщение на экране сбора: штуки, чей КИЗ WB не принял
 *  (забраковал, не закрепил, в задании чужой код).
 *
 *  Не тост и само не пропадает: искать одну штуку среди 200–300 в конце, у
 *  кнопки «В доставку», нереально — поэтому говорим сразу, пока короб рядом:
 *  какую наклейку искать, в каком коробе, что за товар и почему. Строка уходит,
 *  когда штуку отправили без кода (или её статус у WB сменился).
 *
 *  Строки — по ВСЕМ открытым поставкам склада, не только поставке экрана:
 *  если среди них есть чужая (другое юрлицо), каждая подписана юрлицом и
 *  поставкой (`sgtinShowSupply`).
 *
 *  `extraAction` — место под второе действие на строке («Заменить товар»). */
export function SgtinRejectedAlert({
  rows,
  currentSupplyId,
  onChanged,
  extraAction,
}: {
  rows: fbs.SgtinRejected[];
  /** Поставка этого экрана — её строки подписывать не нужно. */
  currentSupplyId: string | null;
  /** Действие на строке прошло — перечитать опрос и состояние экрана. */
  onChanged: () => void;
  extraAction?: (row: fbs.SgtinRejected) => React.ReactNode;
}): React.ReactElement | null {
  if (rows.length === 0) return null;
  const showSupply = sgtinShowSupply(rows, currentSupplyId);
  return (
    <section
      role="alert"
      className="max-h-[40vh] overflow-y-auto border-b-2 border-rose-700 bg-rose-50 px-3 py-2"
    >
      <h2 className="flex items-center gap-1.5 text-sm font-bold text-rose-800">
        <AlertTriangle className="size-4 shrink-0" />
        WB не принял КИЗ: {rows.length} шт. — с ними поставка не уйдёт в доставку
      </h2>
      <ul className="mt-1 divide-y divide-rose-200">
        {rows.map((row) => {
          const sticker = sgtinStickerLabel(row);
          const product = [row.article, row.nomenclature_name].filter(Boolean).join(" · ");
          return (
            <li key={row.pick_id} className="py-2">
              {showSupply && (
                <div className="text-xs font-semibold uppercase text-rose-800">
                  {sgtinSupplyLabel(row)}
                </div>
              )}
              <div className="text-lg font-bold leading-tight text-rose-900">{sticker}</div>
              <div className="text-sm font-semibold text-rose-900">
                {row.box_position != null ? `короб №${row.box_position}` : "не в коробе"}
              </div>
              <div className="text-sm text-slate-800">{product || "—"}</div>
              <div className="text-xs text-rose-700">{row.decision_text}</div>
              <div className="mt-1.5 flex flex-wrap items-center gap-2">
                {row.resend && <ResendSgtinButton pickId={row.pick_id} onDone={onChanged} />}
                {row.sgtin_required ? (
                  <span className="text-xs text-rose-800">
                    WB требует КИЗ по этому заданию — без кода отправить нельзя, штуку
                    нужно заменить.
                  </span>
                ) : (
                  <RemoveSgtinButton
                    pickId={row.pick_id}
                    what={`${sticker} · ${product || `задание #${row.wb_order_id}`}`}
                    onRemoved={onChanged}
                  />
                )}
                {extraAction?.(row)}
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

/** «Отправить КИЗ повторно»: WB код не закрепил или в задании чужой — тот же
 *  код уходит в задание ещё раз. Своя ручка (`resendSgtin`), не «Повторить»
 *  «Разбора»: тот чинит причины по порядку и мог бы заняться партией вместо
 *  КИЗ. Тост — по ответу: статус сброшен в «не читали» — значит, отправлен. */
function ResendSgtinButton({
  pickId,
  onDone,
}: {
  pickId: string;
  onDone: () => void;
}): React.ReactElement {
  const mut = useMutation({
    mutationFn: () => fbs.resendSgtin(pickId),
    onSuccess: (row) => {
      if (row.sgtin_decision === null) {
        toast.success("КИЗ отправлен в WB повторно, проверка идёт");
      } else {
        toast.error(`КИЗ не переотправлен: статус «${row.sgtin_decision_text}»`);
      }
      onDone();
    },
    onError: (e) => toast.error(apiErrorText(e, "Отправить КИЗ повторно не удалось")),
  });
  return (
    <Button
      size="sm"
      variant="outline"
      className="text-rose-700"
      onClick={() => mut.mutate()}
      disabled={mut.isPending}
    >
      Отправить КИЗ повторно
    </Button>
  );
}
